// src/features/locations/ExperienceTab.jsx
//
// EXPERIENCE-TAB v2: Three-factor Experience score + compact amenity grid
// + workforce graphics panel.
//
// Layout (top to bottom):
//   1. KPI tile strip — 4 KpiTile cards (Amenities · Commute · Space · Confidence)
//                        + "Edit amenities" button (editor+ only)
//   2. Admin panel — AmenityAdminPanel (inline, editor+, when editOpen)
//   3. Amenity grid — compact 3-column grid (T.tile background, 16 px icons)
//      or empty-state card when no amenities recorded
//   4. Workforce section — Function mix · M&A cohort · Commute distribution
//      (fetched from people profile; empty-state when absent)
//
// Experience Score = 3 factors (normalized when data absent):
//   Amenities   65 %  — weighted catalog sub-score
//   Commute     20 %  — median commute banding (absent when no Workday extract)
//   Space       15 %  — headcount/capacity sharing ratio (absent when no data)
//
// All colors via T design tokens; no raw hex.

import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../components/Modal.jsx";
import { T } from "../../app/tokens.js";
import { OUTBOUND_LINK_STYLE } from "../../v2/pulse/pulseConstants.js";
import { getAmenities, putAmenities } from "../../data/amenitiesClient.js";
import { getPeopleProfile } from "../../data/peopleClient.js";
import {
  AMENITY_CATALOG,
  CATEGORY_ORDER,
  scoreExperience,
} from "../../data/amenityCatalog.js";
import { sortFunctionMix, commuteBandEntries, isSuppressed } from "./PeopleTab.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { log } from "../../utils/logger.js";

// ─── Constants ────────────────────────────────────────────────────────────────

// Bar chart max-width for workforce horizontal bars (px)
const BAR_MAX_WIDTH = 280;

// Neutral palette for M&A cohort bars (ma1→ma4 from the v3 comp).
// No pink — maps to teal / blue / slate / light token sequence.
const MA_COLORS = [T.statCyan, T.blue, T.mid, T.light];

// ─── Commute median helper ────────────────────────────────────────────────────
// Interpolates the 50th-percentile commute time from the band distribution
// when median_min is not directly provided in the profile.
// Band keys must match commuteBandEntries ORDER: "0-15", "15-30", etc.

function computeMedianMinFromBands(bands) {
  if (!bands || typeof bands !== "object") return null;
  // Each band's lo is exclusive (next minute after the previous band's upper bound).
  // This matches the "30-45 min" band meaning ">30 mins up to 45 mins", so the
  // effective lower bound for interpolation is 31, not 30. This produces 34 min
  // for the reference synthetic distribution {0-15:15,15-30:30,30-45:25,45-60:20,60+:10}.
  const BAND_RANGES = [
    { key: "0-15",  lo:  0, hi: 15 },
    { key: "15-30", lo: 16, hi: 30 },
    { key: "30-45", lo: 31, hi: 45 },
    { key: "45-60", lo: 46, hi: 60 },
    { key: "60+",   lo: 61, hi: 80 },
  ];
  const total = BAND_RANGES.reduce((s, b) => s + (bands[b.key] ?? 0), 0);
  if (total === 0) return null;
  let cum = 0;
  for (const b of BAND_RANGES) {
    const pct = bands[b.key] ?? 0;
    const cumNext = cum + pct;
    if (cumNext >= total / 2) {
      const needed = total / 2 - cum;
      return Math.round(b.lo + (needed / pct) * (b.hi - b.lo));
    }
    cum = cumNext;
  }
  return null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PanelCard({ children, style, "data-testid": testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        boxShadow: T.cardShadow,
        padding: "14px 16px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}


function HorizBar({ label, pct, barColor, bgColor, valueLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}>
      <div title={label} style={{
        flex: "0 1 110px",
        minWidth: 0,
        fontSize: T.fsBase,
        color: T.mid,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, background: bgColor, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: 8, background: barColor, borderRadius: 3 }} />
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, flexShrink: 1 }}>
        {valueLabel !== undefined ? valueLabel : `${pct}%`}
      </div>
    </div>
  );
}

// ─── Amenity icon map ─────────────────────────────────────────────────────────
// Compact 16 px icons (Lucide-style stroke only, no fill).

function AmenityIcon({ amenityKey, size = 16 }) {
  const props = {
    width: size, height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (amenityKey) {
    case "hot_lunch":
      return (
        <svg {...props}>
          {/* Fork — two tines, cross-bar, stem */}
          <line x1="6" y1="2" x2="6" y2="22" />
          <path d="M4 2v5h4V2" />
          {/* Plate */}
          <ellipse cx="12" cy="16" rx="7" ry="4" />
          {/* Knife — tapered blade then straight stem */}
          <path d="M18 2c0 0-2 2-2 6h2V2z" />
          <line x1="18" y1="8" x2="18" y2="22" />
        </svg>
      );
    case "gym":
      return (
        <svg {...props}>
          <path d="M6 5v14" /><path d="M18 5v14" /><path d="M6 9h12" /><path d="M6 15h12" />
          <path d="M2 9h4" /><path d="M18 9h4" /><path d="M2 15h4" /><path d="M18 15h4" />
        </svg>
      );
    case "barista":
      return (
        <svg {...props}>
          <path d="M17 8h1a4 4 0 1 1 0 8h-1" /><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
          <line x1="6" y1="2" x2="6" y2="4" /><line x1="10" y1="2" x2="10" y2="4" /><line x1="14" y1="2" x2="14" y2="4" />
        </svg>
      );
    case "snack_bars":
      return (
        <svg {...props}>
          <path d="M3 11 L3 20 a1 1 0 0 0 1 1 h16 a1 1 0 0 0 1-1 v-9" />
          <path d="M3 11 C3 7 7 3 12 3 S21 7 21 11" />
          <line x1="12" y1="3" x2="12" y2="21" /><line x1="3" y1="11" x2="21" y2="11" />
        </svg>
      );
    case "rooftop":
      return (
        <svg {...props}>
          <polyline points="3 7 12 2 21 7" /><line x1="12" y1="2" x2="12" y2="22" />
          <path d="M5 10v10h14V10" /><path d="M8 22v-5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v5" />
        </svg>
      );
    case "massage":
      return (
        <svg {...props}>
          <path d="M4 4h16v12H4z" /><circle cx="12" cy="10" r="3" /><path d="M4 20h16" />
        </svg>
      );
    case "game_room":
      return (
        <svg {...props}>
          <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
          <polyline points="17 2 12 7 7 2" />
          <line x1="9" y1="14" x2="9" y2="14" /><line x1="15" y1="14" x2="15" y2="14" /><line x1="12" y1="11" x2="12" y2="17" />
        </svg>
      );
    case "library":
      return (
        <svg {...props}>
          <path d="M4 19V5a2 2 0 0 1 2-2h13" />
          <path d="M4 19a2 2 0 0 0 2 2h13V7H6a2 2 0 0 0-2 2" />
          <line x1="9" y1="12" x2="15" y2="12" />
        </svg>
      );
    case "bike_commuter":
      return (
        <svg {...props}>
          <circle cx="5" cy="17" r="3" /><circle cx="19" cy="17" r="3" />
          <path d="M5 17H3V7h4l3 6h8" /><polyline points="10 7 14 7 17 14" /><circle cx="14" cy="7" r="1" />
        </svg>
      );
    case "parking":
      return (
        <svg {...props}>
          {/* Steering wheel */}
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3" />
          <line x1="12" y1="3" x2="12" y2="9" />
          <line x1="3" y1="12" x2="9" y2="12" />
          <line x1="15" y1="12" x2="21" y2="12" />
        </svg>
      );
    case "mothers_room":
      return (
        <svg {...props}>
          {/* Person silhouette with heart */}
          <circle cx="12" cy="6" r="2.5" />
          <path d="M8 22v-6a4 4 0 0 1 8 0v6" />
          <path d="M10 13.5c0-1.1.9-2 2-2s2 .9 2 2-2 3.5-2 3.5-2-2.4-2-3.5z" />
        </svg>
      );
    case "it_bar":
      return (
        <svg {...props}>
          {/* Monitor */}
          <rect x="2" y="3" width="14" height="10" rx="1.5" />
          <line x1="9" y1="13" x2="9" y2="16" />
          <line x1="6" y1="16" x2="12" y2="16" />
          {/* Wrench */}
          <path d="M15 9a4 4 0 0 0 4-4 4 4 0 0 0-1.2-2.8l-2.4 2.4-1.4-1.4 2.4-2.4A4 4 0 0 0 11 5a4 4 0 0 0 .8 2.4L8 12a1.4 1.4 0 0 0 2 2l3.6-3.8A4 4 0 0 0 15 9z" />
        </svg>
      );
    case "it_vending":
      return (
        <svg {...props}>
          {/* Vending machine body */}
          <rect x="5" y="2" width="14" height="20" rx="1.5" />
          {/* Display panel */}
          <rect x="7" y="4" width="10" height="6" rx="1" />
          {/* Coin slot */}
          <line x1="15" y1="12" x2="17" y2="12" />
          {/* Selection buttons */}
          <circle cx="9" cy="13" r="0.8" /><circle cx="12" cy="13" r="0.8" />
          {/* Dispenser tray */}
          <path d="M7 17h10v3H7z" />
          <line x1="12" y1="17" x2="12" y2="20" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
  }
}

// ─── Amenity pane (categorized single-card layout) ────────────────────────────

function buildDetailLine(entry, detail) {
  if (!detail) return null;
  if (entry.hasFrequency && detail.daysPerWeek != null) return `${detail.daysPerWeek}×/week`;
  if (entry.hasCount     && detail.count       != null) return `${detail.count} on-site`;
  return null;
}

function AmenityItem({ entry, detail }) {
  const detailLine = buildDetailLine(entry, detail);
  return (
    <div
      data-testid={`amenity-item-${entry.key}`}
      style={{ display: "flex", alignItems: "center", gap: 11 }}
    >
      <span style={{ display: "flex", alignItems: "center", color: T.dark, flexShrink: 0 }}>
        <AmenityIcon amenityKey={entry.key} size={19} />
      </span>
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: T.dark }}>
        {entry.label}
        {detailLine && (
          <span style={{ fontSize: T.fsXs, color: T.light, marginLeft: 2 }}>
            {detailLine}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * splitCategories — pure helper, exported for unit testing.
 *
 * Finds the contiguous cutpoint k (preserving CATEGORY_ORDER adjacency) that
 * minimises |leftSum − rightSum|, then swaps if the right bucket is heavier so
 * the left column always has the greater (or equal) row count.
 *
 * @param {Array<{cat: string, rowCount: number}>} items
 * @returns {{ leftCats: string[], rightCats: string[] }}
 */
export function splitCategories(items) {
  if (items.length === 0) return { leftCats: [], rightCats: [] };

  const total = items.reduce((s, { rowCount }) => s + rowCount, 0);

  let bestK = 0;
  let bestDiff = Infinity;
  let runningSum = 0;

  for (let k = 0; k <= items.length; k++) {
    const diff = Math.abs(runningSum - (total - runningSum));
    if (diff <= bestDiff) {
      bestDiff = diff;
      bestK = k;
    }
    if (k < items.length) {
      runningSum += items[k].rowCount;
    }
  }

  const leftSlice  = items.slice(0, bestK).map(({ cat }) => cat);
  const rightSlice = items.slice(bestK).map(({ cat }) => cat);

  const leftSum  = items.slice(0, bestK).reduce((s, { rowCount }) => s + rowCount, 0);
  const rightSum = total - leftSum;

  if (rightSum > leftSum) {
    return { leftCats: rightSlice, rightCats: leftSlice };
  }
  return { leftCats: leftSlice, rightCats: rightSlice };
}

const AMENITY_COLLAPSE_THRESHOLD = 8;

function AmenityPane({ presentAmenities, amenities }) {
  const [expanded, setExpanded] = React.useState(false);

  const sorted = CATEGORY_ORDER.flatMap((cat) =>
    presentAmenities.filter((e) => (e.category ?? "Other") === cat)
  );

  const total = sorted.length;
  const visible = expanded || total <= AMENITY_COLLAPSE_THRESHOLD
    ? sorted
    : sorted.slice(0, AMENITY_COLLAPSE_THRESHOLD);

  return (
    <PanelCard data-testid="amenity-pane">
      <PaneHeading>
        Amenities · {total} on-site
      </PaneHeading>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: "14px 32px",
      }}>
        {visible.map((entry) => (
          <AmenityItem
            key={entry.key}
            entry={entry}
            detail={amenities[entry.key]?.detail ?? null}
          />
        ))}
      </div>
      {total > AMENITY_COLLAPSE_THRESHOLD && !expanded && (
        <span
          data-testid="amenity-see-all"
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(true)}
          onKeyDown={(e) => e.key === "Enter" && setExpanded(true)}
          style={{
            ...OUTBOUND_LINK_STYLE,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            marginTop: 16,
          }}
        >
          See all {total} amenities ›
        </span>
      )}
    </PanelCard>
  );
}

// ─── Toggle switch (pill-shaped, no extra deps) ───────────────────────────────

function AmenityToggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={onChange}
      style={{
        position: "relative",
        width: 38,
        height: 21,
        borderRadius: 11,
        background: checked ? T.blue : T.border,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: 0,
        flexShrink: 0,
        transition: "background 0.15s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 19 : 2,
          width: 17,
          height: 17,
          borderRadius: 9,
          background: T.white,
          boxShadow: T.shadowToggle,
          transition: "left 0.15s",
          display: "block",
        }}
      />
    </button>
  );
}

// ─── Amenity admin picker ─────────────────────────────────────────────────────

const AMENITY_MODAL_TITLE_ID = "edit-amenities-modal-title";

function AmenityAdminPanel({ locationId, currentAmenities, onSaved, onClose }) {
  const [draft, setDraft]         = useState(() => buildDraft(currentAmenities));
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setDraft(buildDraft(currentAmenities));
  }, [currentAmenities]);

  function buildDraft(amenities) {
    const result = {};
    for (const entry of AMENITY_CATALOG) {
      const existing = amenities?.[entry.key] ?? { present: false };
      result[entry.key] = {
        present:     Boolean(existing.present),
        daysPerWeek: existing.detail?.daysPerWeek ?? "",
        count:       existing.detail?.count ?? "",
      };
    }
    return result;
  }

  function togglePresent(key) {
    setDraft((d) => ({ ...d, [key]: { ...d[key], present: !d[key].present } }));
  }

  function setDetail(key, field, value) {
    setDraft((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);

    const payload = {};
    for (const entry of AMENITY_CATALOG) {
      const row = draft[entry.key];
      if (!row) continue;
      const obj    = { present: row.present };
      const detail = {};
      if (entry.hasFrequency && row.daysPerWeek !== "" && row.daysPerWeek != null) {
        detail.daysPerWeek = Number(row.daysPerWeek);
      }
      if (entry.hasCount && row.count !== "" && row.count != null) {
        detail.count = Number(row.count);
      }
      if (Object.keys(detail).length > 0) obj.detail = detail;
      payload[entry.key] = obj;
    }

    try {
      const updated = await putAmenities(locationId, payload);
      onSaved(updated);
    } catch (err) {
      log.error("[ExperienceTab] amenity save failed", { id: locationId, err: String(err) });
      setSaveError(err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="amenity-admin-panel">
      {saveError && (
        <div
          role="alert"
          data-testid="amenity-save-error"
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            background: T.redSoft,
            border: `1px solid ${T.redLight}`,
            borderRadius: 6,
            color: T.red,
            fontSize: T.fsSm,
          }}
        >
          {saveError}
        </div>
      )}
      <div>
        {CATEGORY_ORDER.map((cat) => {
          const entries = AMENITY_CATALOG.filter((e) => e.category === cat);
          if (entries.length === 0) return null;
          return (
            <div key={cat}>
              <div
                style={{
                  fontSize: T.fsXxs,
                  fontWeight: T.fwBold,
                  color: T.mid,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  margin: "16px 0 2px",
                }}
              >
                {cat}
              </div>
              {entries.map((entry, idx) => {
                const row = draft[entry.key] ?? { present: false, daysPerWeek: "", count: "" };
                const muted = !row.present || saving;
                return (
                  <div
                    key={entry.key}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 11,
                      padding: "11px 0",
                      borderTop: idx === 0 ? "none" : `1px solid ${T.hair}`,
                    }}
                  >
                    {/* Icon chip */}
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        background: T.tile,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        color: muted ? T.light : T.mid,
                        lineHeight: 0,
                      }}
                    >
                      <AmenityIcon amenityKey={entry.key} size={15} />
                    </span>
                    {/* Body */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: T.fsBase5,
                          fontWeight: T.fwSemibold,
                          color: muted ? T.light : T.dark,
                        }}
                      >
                        {entry.label}
                      </div>
                      {row.present && entry.hasFrequency && (
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 5 }}>
                          <input
                            type="number"
                            data-testid={`amenity-freq-${entry.key}`}
                            min={0} max={7}
                            value={row.daysPerWeek}
                            onChange={(e) => setDetail(entry.key, "daysPerWeek", e.target.value)}
                            disabled={saving}
                            placeholder="0"
                            style={{
                              width: 46,
                              fontSize: T.fsSm5,
                              padding: "3px 7px",
                              border: `1px solid ${T.borderDark}`,
                              borderRadius: 6,
                              textAlign: "center",
                            }}
                          />
                          <span style={{ fontSize: T.fsXs, color: T.light }}>days / week</span>
                        </div>
                      )}
                      {row.present && entry.hasCount && (
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 5 }}>
                          <input
                            type="number"
                            data-testid={`amenity-count-${entry.key}`}
                            min={0}
                            value={row.count}
                            onChange={(e) => setDetail(entry.key, "count", e.target.value)}
                            disabled={saving}
                            placeholder="0"
                            style={{
                              width: 46,
                              fontSize: T.fsSm5,
                              padding: "3px 7px",
                              border: `1px solid ${T.borderDark}`,
                              borderRadius: 6,
                              textAlign: "center",
                            }}
                          />
                          <span style={{ fontSize: T.fsXs, color: T.light }}>on-site</span>
                        </div>
                      )}
                    </div>
                    {/* Toggle */}
                    <div style={{ marginTop: 3 }}>
                      <AmenityToggle
                        checked={row.present}
                        onChange={() => togglePresent(entry.key)}
                        disabled={saving}
                        testId={`amenity-check-${entry.key}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          data-testid="amenity-cancel-btn"
          disabled={saving}
          onClick={onClose}
          style={{
            background: T.white,
            color: T.mid,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            padding: "9px 16px",
            fontSize: T.fsBase,
            fontWeight: T.fwSemibold,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="amenity-save-btn"
          disabled={saving}
          onClick={handleSave}
          style={{
            background: saving ? T.lightGray : T.blue,
            color: saving ? T.mid : T.white,
            border: "none",
            borderRadius: 8,
            padding: "9px 16px",
            fontSize: T.fsBase,
            fontWeight: T.fwSemibold,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save amenities"}
        </button>
      </div>
    </div>
  );
}

// ─── Workforce section ────────────────────────────────────────────────────────
// Renders Function Mix · M&A Cohort · Commute distribution from the people
// profile. Shows a neutral empty-state card when no profile data is available
// (Workday extract not yet connected).

// Number of function rows shown by default before collapse kicks in.
const FUNCTION_MIX_VISIBLE = 6;

function WorkforceSection({ profile }) {
  const [showAllFunctions, setShowAllFunctions] = useState(false);

  if (!profile || isSuppressed(profile)) {
    return (
      <PanelCard>
        <div
          data-testid="workforce-empty-state"
          style={{ color: T.mid, fontSize: T.fsSm5, textAlign: "center", padding: "10px 0" }}
        >
          No workforce data available
        </div>
      </PanelCard>
    );
  }

  const { functionMix, maCohorts, commute, commuteProvenance } = profile;
  const sortedFunctions = sortFunctionMix(functionMix);
  const commuteBands    = commuteBandEntries(commute?.bands ?? {});
  // Category-max scaling: largest band fills 100% of the track.
  const maxCommutePct   = commuteBands.length > 0
    ? Math.max(...commuteBands.map((b) => b.pct))
    : 1;
  const isSynthetic     = commuteProvenance === "synthetic";
  const medianMin       = commute?.medianMin ?? null;
  const pctOver45       = commute?.pctOver45 ?? null;
  const maxMaCount      = Array.isArray(maCohorts) && maCohorts.length > 0
    ? Math.max(...maCohorts.map((c) => c.count))
    : 0;

  // Function Mix collapse: show top FUNCTION_MIX_VISIBLE rows by default.
  // sortedFunctions is already sorted descending by pct, so sortedFunctions[0]
  // is the maximum — scale all bars relative to it (largest = 100% track width).
  const maxFnPct         = sortedFunctions.length > 0 ? sortedFunctions[0].pct : 1;
  const hiddenCount      = Math.max(0, sortedFunctions.length - FUNCTION_MIX_VISIBLE);
  const visibleFunctions = showAllFunctions
    ? sortedFunctions
    : sortedFunctions.slice(0, FUNCTION_MIX_VISIBLE);

  const panelCardStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: 300,
  };

  return (
    <div
      data-testid="workforce-section"
      style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, alignItems: "stretch" }}
    >
      {/* Col 1 — Function Mix */}
      <PanelCard style={panelCardStyle}>
        <PaneHeading>Function mix</PaneHeading>
        {sortedFunctions.length === 0 ? (
          <div style={{ color: T.light, fontSize: T.fsSm }}>No function data.</div>
        ) : (
          <>
            {visibleFunctions.map((item) => (
              <HorizBar
                key={item.function}
                label={item.function}
                pct={Math.round((item.pct / maxFnPct) * 100)}
                barColor={T.purple}
                bgColor={T.purpleBg}
                valueLabel={`${item.pct}%`}
              />
            ))}
            {hiddenCount > 0 && (
              <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 4, paddingTop: 6 }}>
                <button
                  data-testid="function-mix-toggle"
                  onClick={() => setShowAllFunctions((v) => !v)}
                  style={{
                    ...OUTBOUND_LINK_STYLE,
                  }}
                >
                  {showAllFunctions
                    ? "Show less ▴"
                    : `Show ${hiddenCount} more ▾`}
                </button>
              </div>
            )}
          </>
        )}
      </PanelCard>

      {/* Col 2 — M&A Cohort (neutral palette, no pink) */}
      <PanelCard style={panelCardStyle}>
        <PaneHeading>M&A cohort</PaneHeading>
        {!Array.isArray(maCohorts) || maCohorts.length === 0 ? (
          <div style={{ color: T.light, fontSize: T.fsSm }}>No M&A cohort data.</div>
        ) : (
          maCohorts.map((cohort, idx) => {
            const pct = maxMaCount > 0 ? Math.round((cohort.count / maxMaCount) * 100) : 0;
            return (
              <HorizBar
                key={cohort.company}
                label={cohort.company}
                pct={pct}
                barColor={MA_COLORS[idx % MA_COLORS.length]}
                bgColor={T.lightGray}
                valueLabel={cohort.count}
              />
            );
          })
        )}
      </PanelCard>

      {/* Col 3 — Commute distribution */}
      <PanelCard style={panelCardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <PaneHeading>Commute</PaneHeading>
          {isSynthetic && (
            <span
              data-testid="commute-synthetic-badge"
              style={{
                fontSize: T.fsXxxs,
                fontWeight: T.fwBold,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: T.amberDark,
                background: T.warnAmberBg,
                border: `1px solid ${T.decisionsCardBorder}`,
                borderRadius: 4,
                padding: "1px 5px",
                marginTop: -8,
              }}
            >
              SYNTHETIC
            </span>
          )}
        </div>
        {(medianMin != null || pctOver45 != null) && (
          <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 8 }}>
            Median {medianMin != null ? `${medianMin} min` : "—"} · {pctOver45 != null ? `${pctOver45}%` : "—"} over 45 min
          </div>
        )}
        {commuteBands.length === 0 ? (
          <div style={{ color: T.light, fontSize: T.fsSm }}>No commute data.</div>
        ) : (
          commuteBands.map((band) => (
            <HorizBar
              key={band.label}
              label={`${band.label} min`}
              pct={Math.round((band.pct / maxCommutePct) * 100)}
              barColor={T.statCyan}
              bgColor={T.blueSoft}
              valueLabel={`${band.pct}%`}
            />
          ))
        )}
      </PanelCard>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * ExperienceTab
 *
 * @param {string}  locationId        — canonical location ID
 * @param {boolean} canWrite          — true when currentRole is admin or editor
 * @param {object}  [location]        — full location row (for seat avail KPI)
 * @param {boolean} [editOpen]        — controlled amenity edit panel open state
 * @param {Function} [onEditOpenChange] — called with next open state when button clicked
 */
export function ExperienceTab({ locationId, canWrite, location, editOpen: editOpenProp, onEditOpenChange }) {
  const [data, setData]             = useState(undefined); // undefined=loading, null=error, object=loaded
  const [fetchError, setFetchError] = useState(null);
  const [editOpenInternal, setEditOpenInternal] = useState(false);
  const [profile, setProfile]       = useState(null); // people profile; null = absent/unknown

  // Support both controlled (prop-driven) and uncontrolled edit open state.
  const editOpen = editOpenProp !== undefined ? editOpenProp : editOpenInternal;
  function setEditOpen(next) {
    if (onEditOpenChange) onEditOpenChange(next);
    else setEditOpenInternal(next);
  }

  const fetchData = useCallback(() => {
    let cancelled = false;
    setFetchError(null);

    // Primary fetch: amenities (determines loading / error states)
    const amenityFetch = getAmenities(locationId)
      .then((row) => { if (!cancelled) setData(row ?? null); })
      .catch((err) => {
        if (cancelled) return;
        log.error("[ExperienceTab] fetch failed", { id: locationId, err: String(err) });
        setFetchError(err);
        setData(null);
      });

    // Secondary fetch: people profile (non-blocking — error → show empty-state)
    getPeopleProfile(locationId)
      .then((p) => { if (!cancelled) setProfile(p); })
      .catch((err) => {
        log.warn("[ExperienceTab] people profile fetch failed", { id: locationId, err: String(err) });
        // Non-fatal: workforce section will show empty-state
      });

    return () => { cancelled = true; };
  }, [locationId]);

  useEffect(() => {
    setData(undefined);
    setEditOpen(false);
    setProfile(null);
    const cancel = fetchData();
    return cancel;
  }, [locationId, fetchData]);

  function handleSaved(updated) {
    setData(updated);
    setEditOpen(false);
  }

  // ── Loading ──
  if (data === undefined && !fetchError) {
    return (
      <div data-testid="experience-tab-loading" style={{ padding: 24, color: T.light, fontSize: T.fsBase }}>
        Loading experience data…
      </div>
    );
  }

  // ── Error ──
  if (fetchError) {
    return (
      <div data-testid="experience-tab-error" style={{ padding: 8 }}>
        <ErrorBanner error={fetchError} />
      </div>
    );
  }

  // ── Compute score ────────────────────────────────────────────────────────────
  const amenities    = data?.amenities ?? null;
  // Scoring uses ONLY the authoritative medianMin from the profile.
  // Band-interpolated commute is for KPI display only (never fed to scoreExperience).
  // NOTE: profile arrives from GET /api/people/profiles/:id which passes through
  // rowToCamel — all JSONB nested keys are camelCased (median_min → medianMin).
  const _scoringMedian = profile?.commute?.medianMin ?? null;
  const commuteData  = _scoringMedian != null ? { median_min: _scoringMedian } : null;

  // Space Comfort sub-factor: requires both installed capacity and headcount.
  const installedSeats    = location?.installedCapacity ?? null;
  const assignedHeadcount = location?.latestHeadcount?.total ?? null;
  const spaceData = (installedSeats != null && assignedHeadcount != null)
    ? { sharingRatio: assignedHeadcount / installedSeats }
    : null;

  const scoreResult = scoreExperience(amenities, commuteData, spaceData);
  const {
    confidence,
    amenityCount,
    spaceSubScore,
  } = scoreResult;

  const hasData        = confidence === "computed";
  const presentAmenities = AMENITY_CATALOG.filter(
    (a) => amenities?.[a.key]?.present === true
  );

  // ── KPIs for SubScoreHeader ──────────────────────────────────────────────────

  // Commute: use direct median_min when available, otherwise interpolate from bands.
  const isSyntheticCommute = profile?.commuteProvenance === "synthetic";
  const medianMinDirect    = profile?.commute?.medianMin ?? null;
  const medianMinComputed  = medianMinDirect != null
    ? medianMinDirect
    : computeMedianMinFromBands(profile?.commute?.bands ?? null);

  // Seat availability: installed seats minus assigned headcount (computed above).
  const seatAvail = (installedSeats != null && assignedHeadcount != null)
    ? installedSeats - assignedHeadcount
    : null;

  const kpis = [
    {
      testid: "experience-kpi-amenities",
      label:  "Amenities",
      value:  amenityCount,
      sub:    `of ${AMENITY_CATALOG.length}`,
      color:  T.blue,
    },
    {
      testid: "experience-kpi-commute",
      label:  "Avg commute",
      value:  medianMinComputed != null ? `${medianMinComputed} min` : "—",
      sub:    medianMinComputed != null
        ? (isSyntheticCommute ? "median · synthetic" : "median")
        : undefined,
      color:  T.utilGold,
    },
    {
      testid: "experience-kpi-seat-avail",
      label:  "Seat avail",
      value:  seatAvail != null ? seatAvail : "—",
      sub:    seatAvail != null ? `open of ${installedSeats}` : "no data",
      color:  T.purple,
    },
    {
      testid: "experience-kpi-long-commute",
      label:  "Long commute",
      value:  profile?.commute?.pctOver45 != null ? `${profile.commute.pctOver45}%` : "—",
      sub:    profile?.commute?.pctOver45 != null ? "over 45 min" : "no data",
      color:  T.teal,
    },
  ];

  return (
    <div data-testid="experience-tab" style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* ── KPI tile strip ──────────────────────────────────────────────────── */}
      <div
        data-testid="experience-kpi-strip"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(140px, 1fr))",
          gap: 12,
        }}
      >
        {kpis.map((kpi, i) => (
          <KpiTile
            key={kpi.testid ?? kpi.label}
            testId={kpi.testid}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.sub}
            color={kpi.color}
            tileIndex={i}
          />
        ))}
      </div>

      {/* ── Space Comfort data-availability note ──────────────────────────────── */}
      {spaceSubScore === null && hasData && (
        <div
          data-testid="experience-renorm-note"
          style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic", paddingLeft: 4 }}
        >
          Space Comfort excluded (no headcount or capacity data).
        </div>
      )}

      {/* ── Edit amenities modal ─────────────────────────────────────────────── */}
      <Modal
        isOpen={canWrite && editOpen}
        onClose={() => setEditOpen(false)}
        ariaLabelledBy={AMENITY_MODAL_TITLE_ID}
        testId="edit-amenities-modal"
        overlayTestId="edit-amenities-modal-overlay"
        maxWidth={580}
        title="Edit amenities"
        closeTestId="edit-amenities-modal-close-x"
      >
        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 20px" }}>
          <AmenityAdminPanel
            locationId={locationId}
            currentAmenities={amenities}
            onSaved={handleSaved}
            onClose={() => setEditOpen(false)}
          />
        </div>
      </Modal>

      {/* ── Amenity pane or empty state ───────────────────────────────────────── */}
      {presentAmenities.length > 0 ? (
        <AmenityPane presentAmenities={presentAmenities} amenities={amenities} />
      ) : (
        <PanelCard>
          <div
            data-testid="experience-empty-state"
            style={{ color: T.mid, fontSize: T.fsBase, textAlign: "center", padding: "12px 0" }}
          >
            No amenities recorded for this location yet.
          </div>
        </PanelCard>
      )}

      {/* ── Workforce section ────────────────────────────────────────────────── */}
      <WorkforceSection profile={profile} />

      {/* ── Space comfort placeholder — slim full-width strip ─────────────────── */}
      <div
        data-testid="workforce-placeholder"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          border: `1px dashed ${T.border}`,
          borderRadius: 8,
          padding: "14px 16px",
          color: T.light,
          fontSize: T.fsSm,
        }}
      >
        <span style={{
          fontSize: T.fsXxs,
          background: T.border,
          color: T.mid,
          borderRadius: 5,
          padding: "2px 8px",
          fontWeight: T.fwBold,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          flexShrink: 0,
        }}>
          Coming soon
        </span>
        Space comfort &amp; wellbeing signals
      </div>

    </div>
  );
}
